class WVideo extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.signal = this.controller.signal;
    this.viewport = this.querySelector('.w-video__viewport');
    this.track = this.querySelector('.w-video__track');
    this.dotsWrap = this.querySelector('.w-video__dots');
    this.prevButton = this.querySelector('[data-direction="prev"]');
    this.nextButton = this.querySelector('[data-direction="next"]');
    this.catButtons = Array.from(this.querySelectorAll('.w-video__cat'));
    this.modal = this.querySelector('.w-video__modal');
    this.modalVideo = this.modal?.querySelector('.w-video__modal-video');
    this.desktop = window.matchMedia('(min-width: 990px)');
    if (!this.viewport || !this.track) return;
    const { signal } = this;
    this.loop = this.dataset.loop === 'true';
    this.realCards = Array.from(this.track.children);
    this.index = 0;
    this.animating = false;
    this.catButtons.forEach((btn) =>
      btn.addEventListener('click', () => this.filter(btn), { signal }),
    );
    this.prevButton?.addEventListener('click', () => this.move(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.move(1), { signal });
    this.track.addEventListener('transitionend', (event) => this.onTransitionEnd(event), {
      signal,
    });
    this.viewportWidth = window.innerWidth;
    window.addEventListener(
      'resize',
      () => {
        if (window.innerWidth === this.viewportWidth) return;
        this.viewportWidth = window.innerWidth;
        this.animating = false;
        if (this.animTimer) clearTimeout(this.animTimer);
        this.setPosition(false);
        this.updateUI();
      },
      { signal },
    );
    let startX = null;
    this.viewport.addEventListener(
      'touchstart',
      (event) => {
        startX = event.touches[0].clientX;
      },
      { passive: true, signal },
    );
    this.viewport.addEventListener(
      'touchend',
      (event) => {
        if (startX === null) return;
        const delta = event.changedTouches[0].clientX - startX;
        startX = null;
        if (Math.abs(delta) > 40) this.move(delta < 0 ? 1 : -1);
      },
      { passive: true, signal },
    );
    this.track.addEventListener(
      'click',
      (event) => {
        const player = event.target.closest('.w-video__player');
        if (!player || !this.track.contains(player)) return;
        if (player.classList.contains('is-playing')) return;
        const video = player.querySelector('.w-video__video');
        if (!video) return;
        if (this.modal && this.desktop.matches) {
          this.openModal(video);
          return;
        }
        player.classList.add('is-playing');
        video.setAttribute('controls', 'controls');
        video.play();
      },
      { signal },
    );
    this.modal?.addEventListener(
      'click',
      (event) => {
        if (event.target === this.modal || event.target.closest('[data-close]')) this.closeModal();
      },
      { signal },
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.modal?.classList.contains('is-open')) this.closeModal();
      },
      { signal },
    );
    this.build('*');
  }
  disconnectedCallback() {
    this.controller?.abort();
    document.body.style.overflow = '';
  }
  openModal(cardVideo) {
    if (!this.modal || !this.modalVideo) return;
    this.modalVideo.innerHTML = '';
    cardVideo.querySelectorAll('source').forEach((source) => {
      const clone = document.createElement('source');
      clone.src = source.src;
      if (source.type) clone.type = source.type;
      this.modalVideo.appendChild(clone);
    });
    this.modalVideo.load();
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    const played = this.modalVideo.play();
    if (played) played.catch(() => {});
  }
  closeModal() {
    if (!this.modal) return;
    this.modalVideo?.pause();
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
  build(filterValue) {
    this.index = 0;
    this.animating = false;
    this.track.querySelectorAll('[data-clone]').forEach((clone) => clone.remove());
    this.cards = this.realCards.filter((card) => {
      const match = filterValue === '*' || card.dataset.category === filterValue;
      card.hidden = !match;
      if (!match) {
        const video = card.querySelector('.w-video__video');
        video?.pause();
        video?.removeAttribute('controls');
        card.querySelector('.w-video__player')?.classList.remove('is-playing');
      }
      return match;
    });
    this.count = this.cards.length;
    if (this.loop && this.count > 1) {
      this.cards.forEach((card) => {
        const clone = card.cloneNode(true);
        clone.hidden = false;
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-clone', '');
        this.track.appendChild(clone);
      });
    }
    this.buildDots();
    this.setPosition(false);
    this.updateUI();
  }
  filter(button) {
    this.catButtons.forEach((cat) => {
      const active = cat === button;
      cat.classList.toggle('is-active', active);
      cat.setAttribute('aria-selected', active);
    });
    this.build(button.dataset.filter);
  }
  buildDots() {
    if (!this.dotsWrap) return;
    this.dotsWrap.innerHTML = '';
    this.dots = this.cards.map((card, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'w-carousel-dot';
      dot.setAttribute('aria-label', 'Aller à la vidéo ' + (i + 1));
      dot.addEventListener('click', () => this.select(i), { signal: this.signal });
      this.dotsWrap.appendChild(dot);
      return dot;
    });
  }
  step() {
    if (!this.count) return 0;
    return (
      this.cards[0].getBoundingClientRect().width +
      (parseFloat(getComputedStyle(this.track).columnGap) || 0)
    );
  }
  setPosition(animate) {
    this.track.style.transition = animate ? '' : 'none';
    this.track.style.transform = 'translateX(' + -this.index * this.step() + 'px)';
    if (!animate) {
      void this.track.offsetWidth;
      this.track.style.transition = '';
    }
  }
  activeDot() {
    if (!this.count) return 0;
    return ((this.index % this.count) + this.count) % this.count;
  }
  visibleCount() {
    return Math.max(1, Math.floor((this.viewport.clientWidth + 2) / this.step()));
  }
  maxIndex() {
    return Math.max(0, this.count - this.visibleCount());
  }
  updateUI() {
    const active = this.activeDot();
    this.dots?.forEach((dot, i) => dot.classList.toggle('is-active', i === active));
    const single = this.count <= 1;
    if (this.loop) {
      if (this.prevButton) this.prevButton.disabled = single;
      if (this.nextButton) this.nextButton.disabled = single;
    } else {
      if (this.prevButton) this.prevButton.disabled = single || this.index <= 0;
      if (this.nextButton) this.nextButton.disabled = single || this.index >= this.maxIndex();
    }
  }
  move(direction) {
    if (this.animating || this.count <= 1) return;
    if (this.loop) {
      if (direction === -1 && this.index === 0) {
        this.index = this.count;
        this.setPosition(false);
      }
      this.animateTo(this.index + direction);
      return;
    }
    this.animateTo(Math.max(0, Math.min(this.index + direction, this.maxIndex())));
  }
  select(index) {
    if (this.animating || index === this.activeDot()) return;
    this.animateTo(index);
  }
  animateTo(index) {
    if (index === this.index) return;
    this.animating = true;
    this.index = index;
    this.setPosition(true);
    this.updateUI();
    if (this.animTimer) clearTimeout(this.animTimer);
    this.animTimer = setTimeout(() => {
      this.animating = false;
    }, 600);
  }
  onTransitionEnd(event) {
    if (event.target !== this.track || event.propertyName !== 'transform') return;
    this.animating = false;
    if (this.loop && this.index >= this.count) {
      this.index -= this.count;
      this.setPosition(false);
      this.updateUI();
    }
  }
}
customElements.define('w-video', WVideo);